// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import {
    getGooglePlacesFullAddress,
    GoogleAutoCompleteAddress,
    initGooglePlacesAutoComplete,
} from "@west/App/Services/GooglePlaceAutocompleteService";
import { CustomAppState } from "@west/App/Store/Reducers";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

export interface AddressFieldProps extends TextFieldProps {
    provider?: "none" | "google";
    onAddressSelected?: (address: GoogleAutoCompleteAddress) => void;
}

const mapStateToProps = (state: CustomAppState) => ({
    googleApiKey: getSettingsCollection(state).websiteSettings.googleMapsApiKey,
});

type Props = AddressFieldProps & ReturnType<typeof mapStateToProps>;

const GoogleAddressField = ({ provider = "none", googleApiKey, onAddressSelected, disabled, ...otherProps }: Props) => {
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const localRef = React.useRef(otherProps.ref || null);

    const loadedScript = () => {
        setScriptLoaded(true);
        const autocomplete = initGooglePlacesAutoComplete(localRef.current as any, () => {
            onAddressSelected?.(getGooglePlacesFullAddress(autocomplete));
        });
    };

    const initScript = async () => {
        if (typeof window === "undefined" || provider === "none") {
            return;
        }

        const { google } = window as any;

        if (!google?.maps) {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.src = `//maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
            script.async = true;
            script.id = "google-places";
            script.onload = () => {
                loadedScript();
            };
            document.head.appendChild(script);
        } else if (!google.maps.places) {
            await google.maps.importLibrary("places");
            loadedScript();
        } else {
            loadedScript();
        }
    };

    useEffect(() => {
        initScript();
    }, []);

    return (
        <TextField
            {...otherProps}
            {...{ validationRules: "min:1|max:100" }}
            ref={localRef}
            disabled={(!scriptLoaded && provider !== "none") || disabled}
        />
    );
};

export default connect(mapStateToProps)(GoogleAddressField);
